/**
 * Métricas do modo mock: Analytics (GA4), usuários ativos, uptime e finanças.
 * Os builders recebem o "agora" na chamada para a tela parecer viva.
 */
import type {
  FinanceData,
  FinancePagamento,
  FinancePeriod,
  FinanceRetencao,
  FinanceVencimento,
} from '@/types/finance.types';
import type { UptimeResult, UptimeSlot, UptimeStatus, UptimeSystem } from '@/api/endpoints/uptime';
import db from './db.json';

// ---------------------------------------------------------------------------
// Dashboard: Analytics + usuários ativos (formato cru da API, snake_case)
// ---------------------------------------------------------------------------

function isoDia(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function buildFirebaseAnalytics() {
  const ref = new Date();
  ref.setDate(ref.getDate() - 1); // GA4 atrasa 1 a 2 dias
  return {
    reference_date: isoDia(ref),
    installs_24h: 37,
    uninstalls_24h: 4,
    uninstalls_platform: 'android',
    sessions_24h: 1284,
    sessions_change: 8,
    events_24h: 23517,
    crash_free_rate: 99.4,
  };
}

export const activeUsersWindows = {
  active_1d: 61,
  active_1d_previous: 54,
  active_7d: 148,
  active_15d: 196,
  active_30d: 243,
};

// ---------------------------------------------------------------------------
// Uptime (UptimeRobot)
// ---------------------------------------------------------------------------

const WINDOW_HOURS = 5;
const SLOT_MINUTES = 10;

interface SistemaSpec {
  key: string;
  name: string;
  url: string;
  paused?: boolean;
  avgResponseMs: number | null;
  /** Índices (a partir do fim da janela) com queda ou lentidão. */
  down?: number[];
  degraded?: number[];
  /** Último incidente em minutos atrás (fora da janela, se não houver queda). */
  lastIncidentMinAgo?: number;
}

const sistemas: SistemaSpec[] = [
  {
    key: 'api-curral-tech',
    name: 'API Curral Tech',
    url: 'https://api.curraltech.com/health',
    avgResponseMs: 182,
    degraded: [17],
    lastIncidentMinAgo: 60 * 24 * 9,
  },
  {
    key: 'painel-admin',
    name: 'Painel administrativo',
    url: 'https://admin.curraltech.com',
    avgResponseMs: 96,
  },
  {
    key: 'painel-do-produtor',
    name: 'Painel do produtor',
    url: 'https://painel.curraltech.com',
    avgResponseMs: 241,
    degraded: [4, 5],
    lastIncidentMinAgo: 60 * 24 * 21,
  },
  {
    key: 'site-curral-tech',
    name: 'Site Curral Tech',
    url: 'https://curraltech.com',
    avgResponseMs: 134,
    down: [12, 13],
  },
  {
    key: 'central-de-atendimento',
    name: 'Central de Atendimento',
    url: 'https://chat.curraltech.com',
    avgResponseMs: 318,
    degraded: [22],
    lastIncidentMinAgo: 60 * 24 * 3,
  },
  {
    key: 'api-teste',
    name: 'API de teste',
    url: 'https://api-test.curraltech.com/health',
    avgResponseMs: null,
    paused: true,
  },
];

function pctUp(slots: UptimeSlot[]): number {
  const counted = slots.filter((s) => s.status !== 'unknown' && s.status !== 'paused');
  if (!counted.length) return 100;
  const upish = counted.filter((s) => s.status === 'up' || s.status === 'degraded').length;
  return Math.round((upish / counted.length) * 1000) / 10;
}

export function buildUptime(): UptimeResult {
  const now = Math.floor(Date.now() / 1000);
  const total = Math.round((WINDOW_HOURS * 60) / SLOT_MINUTES);
  const step = SLOT_MINUTES * 60;
  const start = now - total * step;

  const systems: UptimeSystem[] = sistemas.map((s) => {
    const base: UptimeStatus = s.paused ? 'paused' : 'up';
    const slots: UptimeSlot[] = Array.from({ length: total }, (_, i) => {
      const fromEnd = total - 1 - i;
      let status: UptimeStatus = base;
      if (!s.paused && s.degraded?.includes(fromEnd)) status = 'degraded';
      if (!s.paused && s.down?.includes(fromEnd)) status = 'down';
      return { at: start + i * step, status };
    });
    const firstDown = s.down?.length ? Math.max(...s.down) : null;
    const lastIncidentAt =
      firstDown != null
        ? start + (total - 1 - firstDown) * step
        : s.lastIncidentMinAgo != null
          ? now - s.lastIncidentMinAgo * 60
          : null;
    return {
      key: s.key,
      name: s.name,
      url: s.url,
      status: slots[slots.length - 1].status,
      uptimePct: pctUp(slots),
      avgResponseMs: s.avgResponseMs,
      slots,
      lastIncidentAt,
    };
  });

  return { now, windowHours: WINDOW_HOURS, slotMinutes: SLOT_MINUTES, configured: true, systems };
}

// ---------------------------------------------------------------------------
// Finanças (GET /admin/finance) — centavos
// ---------------------------------------------------------------------------

const MESES_PT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const PRECO = { basico: 3990, profissional: 9990 } as const;
const CONTRATOS_ENTERPRISE = [
  { userId: 21, cliente: 'Agropecuária Santa Luzia Ltda', valor: 149000 },
  { userId: 25, cliente: 'Grupo Fontoura Pecuária', valor: 229000 },
  { userId: 35, cliente: 'Fazendas Reunidas Prado', valor: 390000 },
];

// PRNG determinístico (mesmos números a cada reload).
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clientes = db.users.map((u) => ({ id: Number(u.id), name: u.name }));

function liquido(origem: FinancePagamento['origem'], valor: number): number {
  return origem === 'painel' ? valor : Math.round(valor * 0.85);
}

/** Pagamentos dos últimos 12 meses, do mais antigo para o mais recente. */
function gerarPagamentos(now: Date): FinancePagamento[] {
  const rnd = mulberry32(20261016);
  const out: FinancePagamento[] = [];
  for (let back = 11; back >= 0; back--) {
    const ano = now.getFullYear();
    const mes = now.getMonth() - back;
    const diasNoMes = new Date(ano, mes + 1, 0).getDate();
    const limiteDia = back === 0 ? now.getDate() : diasNoMes;

    // Contratos Enterprise: cobrados pelo painel todo dia 5.
    if (limiteDia >= 5) {
      for (const c of CONTRATOS_ENTERPRISE) {
        out.push({
          data: new Date(ano, mes, 5, 10, 0).toISOString(),
          userId: c.userId,
          cliente: c.cliente,
          origem: 'painel',
          plano: 'Enterprise',
          valorCentavos: c.valor,
          liquidoCentavos: c.valor,
        });
      }
    }

    // Renovações e compras nas lojas: crescem ao longo do ano.
    const qtd = Math.round((12 + (11 - back) * 1.6) * (limiteDia / diasNoMes));
    for (let i = 0; i < qtd; i++) {
      const cli = clientes[Math.floor(rnd() * clientes.length)];
      const profissional = rnd() < 0.58;
      const anual = rnd() < 0.12;
      const origem: FinancePagamento['origem'] = rnd() < 0.62 ? 'google' : 'apple';
      const mensal = profissional ? PRECO.profissional : PRECO.basico;
      const valor = anual ? (profissional ? 99900 : 39900) : mensal;
      const dia = 1 + Math.floor(rnd() * limiteDia);
      out.push({
        data: new Date(
          ano,
          mes,
          dia,
          8 + Math.floor(rnd() * 12),
          Math.floor(rnd() * 60)
        ).toISOString(),
        userId: cli.id,
        cliente: cli.name,
        origem,
        plano: profissional ? 'Profissional' : 'Básico',
        valorCentavos: valor,
        liquidoCentavos: liquido(origem, valor),
      });
    }
  }
  return out.sort((a, b) => a.data.localeCompare(b.data));
}

const RETENCAO: Record<FinancePeriod, FinanceRetencao> = {
  mes: { cancelamentos: 3, testesIniciados: 21, testesConvertidos: 7, conversaoPct: 33 },
  trimestre: { cancelamentos: 9, testesIniciados: 58, testesConvertidos: 19, conversaoPct: 33 },
  ano: { cancelamentos: 27, testesIniciados: 214, testesConvertidos: 66, conversaoPct: 31 },
};

function gerarVencimentos(now: Date): FinanceVencimento[] {
  const dias = [1, 3, 4, 7, 9, 12, 16, 19, 23, 27];
  const quem = [11, 20, 18, 21, 23, 15, 40, 35, 26, 12];
  const origens: FinanceVencimento['origem'][] = [
    'google',
    'google',
    'apple',
    'painel',
    'google',
    'apple',
    'apple',
    'painel',
    'google',
    'google',
  ];
  return dias.map((d, i) => {
    const u = db.users.find((x) => Number(x.id) === quem[i]);
    const origem = origens[i];
    const enterprise = origem === 'painel';
    const profissional = !enterprise && i % 3 !== 0;
    const venceEm = new Date(now.getTime() + d * 24 * 60 * 60 * 1000);
    return {
      userId: quem[i],
      cliente: enterprise
        ? (CONTRATOS_ENTERPRISE.find((c) => c.userId === quem[i])?.cliente ?? u?.name ?? null)
        : (u?.name ?? null),
      origem,
      plano: enterprise ? 'Enterprise' : profissional ? 'Profissional' : 'Básico',
      venceEm: venceEm.toISOString(),
      valorCentavos: enterprise
        ? (CONTRATOS_ENTERPRISE.find((c) => c.userId === quem[i])?.valor ?? 149000)
        : profissional
          ? PRECO.profissional
          : PRECO.basico,
    };
  });
}

export function buildFinance(periodParam: unknown): FinanceData {
  const period: FinancePeriod =
    periodParam === 'trimestre' || periodParam === 'ano' ? periodParam : 'mes';
  const now = new Date();

  // Base de assinantes hoje.
  const basico = { assinantes: 64, pagantes: 51 };
  const profissional = { assinantes: 87, pagantes: 74 };
  const trials = 38;
  const totalUsers = 312;

  const receitaBasico = basico.pagantes * PRECO.basico;
  const receitaProf = profissional.pagantes * PRECO.profissional;
  const receitaEnt = CONTRATOS_ENTERPRISE.reduce((acc, c) => acc + c.valor, 0);
  const mrr = receitaBasico + receitaProf + receitaEnt;
  const pct = (v: number) => Math.round((v / mrr) * 100);

  const pagantes = basico.pagantes + profissional.pagantes + CONTRATOS_ENTERPRISE.length;
  const cortesias =
    basico.assinantes - basico.pagantes + (profissional.assinantes - profissional.pagantes);
  const naoPagantesCount = totalUsers - pagantes;
  const semPlano = naoPagantesCount - cortesias - trials;

  const usuariosPorPlano = [
    { label: 'Trial / sem plano', count: trials + semPlano },
    { label: 'Profissional', count: profissional.pagantes },
    { label: 'Básico', count: basico.pagantes },
    { label: 'Cortesia', count: cortesias },
    { label: 'Enterprise', count: CONTRATOS_ENTERPRISE.length },
  ].sort((a, b) => b.count - a.count);

  const pagamentos = gerarPagamentos(now);
  const voltar = period === 'ano' ? 11 : period === 'trimestre' ? 2 : 0;
  const inicio = new Date(now.getFullYear(), now.getMonth() - voltar, 1);
  const doPeriodo = pagamentos.filter((pg) => new Date(pg.data) >= inicio);

  // Evolução: MRR crescendo até o valor de hoje; recebido sai dos pagamentos.
  const nMonths = period === 'ano' ? 12 : period === 'trimestre' ? 6 : 7;
  const novas = [9, 11, 8, 14, 12, 15, 13, 17, 16, 19, 18, 22];
  const evolucao = Array.from({ length: nMonths }, (_, idx) => {
    const back = nMonths - 1 - idx;
    const ini = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const fim = new Date(now.getFullYear(), now.getMonth() - back + 1, 1);
    const fator = 1 - back * 0.055;
    const recebido = pagamentos
      .filter((pg) => {
        const d = new Date(pg.data);
        return d >= ini && d < fim;
      })
      .reduce((acc, pg) => acc + pg.valorCentavos, 0);
    return {
      mes: MESES_PT[ini.getMonth()],
      mrrCentavos: Math.round(mrr * fator),
      recebidoCentavos: recebido,
      novasAssinaturas: novas[11 - back] ?? 10,
    };
  });
  const mrrMesPassado = evolucao.length >= 2 ? evolucao[evolucao.length - 2].mrrCentavos : mrr;

  return {
    period,
    geradoEm: now.toISOString(),
    mrr: { totalCentavos: mrr, deltaCentavos: mrr - mrrMesPassado },
    pagantes: { count: pagantes, totalUsers, pct: Math.round((pagantes / totalUsers) * 100) },
    naoPagantes: { count: naoPagantesCount, cortesias, trials, semPlano },
    ticketMedioCentavos: Math.round(mrr / pagantes),
    usuariosPorPlano,
    planos: [
      {
        codigo: 'enterprise',
        nome: 'Enterprise',
        precoLabel: 'contrato',
        assinantes: CONTRATOS_ENTERPRISE.length,
        pagantes: CONTRATOS_ENTERPRISE.length,
        receitaMesCentavos: receitaEnt,
        pctMrr: pct(receitaEnt),
      },
      {
        codigo: 'basico',
        nome: 'Básico',
        precoLabel: 'R$ 39,90/mês',
        assinantes: basico.assinantes,
        pagantes: basico.pagantes,
        receitaMesCentavos: receitaBasico,
        pctMrr: pct(receitaBasico),
      },
      {
        codigo: 'profissional',
        nome: 'Profissional',
        precoLabel: 'R$ 99,90/mês',
        assinantes: profissional.assinantes,
        pagantes: profissional.pagantes,
        receitaMesCentavos: receitaProf,
        pctMrr: pct(receitaProf),
      },
      {
        codigo: 'trial',
        nome: 'Teste 15 dias',
        precoLabel: 'grátis',
        assinantes: trials,
        pagantes: 0,
        receitaMesCentavos: 0,
        pctMrr: 0,
      },
    ],
    evolucao,
    recebido: {
      totalCentavos: doPeriodo.reduce((acc, pg) => acc + pg.valorCentavos, 0),
      liquidoCentavos: doPeriodo.reduce((acc, pg) => acc + (pg.liquidoCentavos ?? 0), 0),
      pagamentos: doPeriodo.length,
      clientes: new Set(doPeriodo.map((pg) => pg.userId)).size,
      desde: inicio.toISOString(),
      itens: doPeriodo.slice().reverse(),
    },
    arrCentavos: mrr * 12,
    retencao: RETENCAO[period],
    vencendo: gerarVencimentos(now),
  };
}
